import { useMemo, useEffect } from 'react'
import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Underline from '@tiptap/extension-underline'
import { TextStyle } from '@tiptap/extension-text-style'
import { BulletList } from '@tiptap/extension-bullet-list'
import { OrderedList } from '@tiptap/extension-ordered-list'

const FONT_SIZES = [
  { label: 'Small', value: '0.95rem' },
  { label: 'Normal', value: '1rem' },
  { label: 'Large', value: '1.15rem' },
  { label: 'Extra', value: '1.3rem' }
]

const DispatchEditor = ({ content, onChange }) => {
  const extensions = useMemo(() => [
    StarterKit.configure({
      bulletList: false,
      orderedList: false,
      underline: false
    }),
    Underline,
    TextStyle,
    BulletList.configure({ HTMLAttributes: { class: 'list-disc pl-5' } }),
    OrderedList.configure({ HTMLAttributes: { class: 'list-decimal pl-5' } })
  ], [])

  const editor = useEditor({
    extensions,
    content: content || '<p></p>',
    autofocus: true,
    editorProps: {
      attributes: {
        class:
          'min-h-[320px] w-full rounded-lg border border-border bg-white px-4 py-3 focus:outline-none focus:ring-2 focus:ring-brand-green prose prose-sm max-w-none [&_p]:mb-4 [&_h3]:mt-6 [&_h3]:mb-3 [&_ul]:mb-4 [&_ul]:mt-2 [&_li]:mb-2'
      }
    },
    onUpdate: ({ editor }) => {
      const html = editor.getHTML()
      onChange?.(html)
    }
  }, [extensions])

  useEffect(() => {
    if (editor && typeof content === 'string' && content !== editor.getHTML()) {
      editor.commands.setContent(content, false)
    }
  }, [content, editor])

  const activeFontSize = useMemo(() => {
    if (!editor) return '1rem'
    const dom = editor.view.dom
    const style = window.getComputedStyle(dom.querySelector('p') || dom)
    return style.fontSize || '1rem'
  }, [editor])

  if (!editor) return null

  const applyFontSize = (size) => {
    editor.chain().focus().setMark('textStyle', { fontSize: size }).run()
  }

  const toggleBulletList = () => {
    editor.chain().focus().toggleBulletList().run()
  }

  const toggleOrderedList = () => {
    editor.chain().focus().toggleOrderedList().run()
  }

  const toggleBold = () => {
    editor.chain().focus().toggleBold().run()
  }

  const toggleItalic = () => {
    editor.chain().focus().toggleItalic().run()
  }

  const toggleUnderline = () => {
    editor.chain().focus().toggleUnderline().run()
  }

  const applyHeading = (level) => {
    editor.chain().focus().toggleHeading({ level }).run()
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2 rounded-lg border border-border bg-brand-cream px-3 py-2 text-sm">
        <select
          className="rounded border border-border bg-white px-2 py-1 text-sm"
          value={activeFontSize}
          onChange={(e) => applyFontSize(e.target.value)}
        >
          {FONT_SIZES.map((size) => (
            <option key={size.value} value={size.value}>
              {size.label}
            </option>
          ))}
        </select>
        <ToolbarButton onClick={toggleBold} active={editor.isActive('bold')} label="Bold" />
        <ToolbarButton onClick={toggleItalic} active={editor.isActive('italic')} label="Italic" />
        <ToolbarButton onClick={toggleUnderline} active={editor.isActive('underline')} label="Underline" />
        <ToolbarButton
          onClick={() => applyHeading(3)}
          active={editor.isActive('heading', { level: 3 })}
          label="H3"
        />
        <ToolbarButton
          onClick={() => applyHeading(4)}
          active={editor.isActive('heading', { level: 4 })}
          label="H4"
        />
        <ToolbarButton onClick={toggleBulletList} active={editor.isActive('bulletList')} label="Bullets" />
        <ToolbarButton
          onClick={toggleOrderedList}
          active={editor.isActive('orderedList')}
          label="Numbered"
        />
      </div>
      <div className="rounded-lg border border-border bg-white">
        <EditorContent editor={editor} />
      </div>
    </div>
  )
}

const ToolbarButton = ({ onClick, active, label }) => (
  <button
    type="button"
    onClick={onClick}
    className={`rounded px-2 py-1 text-sm font-medium transition ${
      active ? 'bg-brand-green text-white' : 'bg-white text-brand-charcoal hover:bg-brand-cream'
    }`}
  >
    {label}
  </button>
)

export default DispatchEditor
